import { getAuthorizedFetchOptions } from '../../../helpers/getAuthorizedFetchOptions';
import { ClientInfo, defaultClientInfo } from '../clientInfo';
import { ConversationSession } from '../conversation/types';
import { TriageCase, TriageContact, TriagePayload, TriageReason, TriageService, TriageUser } from './types';

/** Matches MaxConversationTranscript in Kois.Api ChatController. */
export const DEFAULT_MAX_TRANSCRIPT = 200;

export interface BuildTriagePayloadArgs {
  session: ConversationSession;
  user: TriageUser;
  reason: TriageReason;
  selectedCase?: TriageCase;
  userMessage?: string;
  contact?: TriageContact;
  priority?: TriagePayload['priority'];
  sentiment?: TriagePayload['sentiment'];
  maxTranscript?: number;
  now?: () => number;
  client?: Partial<ClientInfo>;
}

/** Pure: turns the current session into the payload a triage API receives. */
export function buildTriagePayload({
  session,
  user,
  reason,
  selectedCase,
  userMessage,
  contact,
  priority,
  sentiment,
  maxTranscript = DEFAULT_MAX_TRANSCRIPT,
  now = Date.now,
  client,
}: BuildTriagePayloadArgs): TriagePayload {
  const recent = session.messages.slice(-maxTranscript);
  const lastWithIntent = [...session.messages].reverse().find((m) => m.intentId);

  return {
    schemaVersion: 1,
    sessionId: session.id,
    reason,
    createdAt: new Date(now()).toISOString(),
    user,
    selectedCase,
    transcript: recent.map((m) => ({
      role: m.role,
      text: m.text,
      createdAt: new Date(m.createdAt).toISOString(),
      intentId: m.intentId,
      ...(m.feedback ? { feedback: m.feedback } : {}),
    })),
    lastIntentId: lastWithIntent?.intentId,
    userMessage,
    contact,
    ...(priority ? { priority } : {}),
    ...(sentiment ? { sentiment } : {}),
    ...(session.ticket?.ref ? { ticketRef: session.ticket.ref, ticketToken: session.ticket.token } : {}),
    client: { ...defaultClientInfo(), ...(client || {}) },
  };
}

export interface CreateTriageServiceOptions {
  endpoint?: string;
  fetchImpl?: typeof fetch;
  logger?: Pick<Console, 'info' | 'warn'>;
}

/**
 * Sends triage payloads to `REACT_APP_CHAT_TRIAGE_END_POINT`. With no endpoint configured it logs
 * the payload and reports `mode: 'noop'`, so the chat flow works before the API exists.
 * Never throws: failures come back as `{ accepted: false }`.
 */
export function createTriageService({
  endpoint = process.env.REACT_APP_CHAT_TRIAGE_END_POINT || '',
  fetchImpl,
  logger = console,
}: CreateTriageServiceOptions = {}): TriageService {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);

  return {
    async submit(payload) {
      const target = (endpoint || '').trim();
      if (!target) {
        logger.info('[askAi] triage request (no endpoint configured, not sent)', payload);
        return { accepted: true, mode: 'noop' };
      }
      if (!doFetch) {
        return { accepted: false, mode: 'remote', error: 'fetch unavailable' };
      }
      try {
        const response = await doFetch(target, getAuthorizedFetchOptions('POST', payload) as RequestInit);
        if (!response.ok) {
          return { accepted: false, mode: 'remote', error: `HTTP ${response.status}` };
        }
        let ticketRef: string | undefined;
        let ticketUrl: string | undefined;
        let ticketToken: string | undefined;
        try {
          const body = await response.json();
          ticketRef = body?.ticketRef || body?.ticketNumber || body?.id;
          if (ticketRef !== undefined) ticketRef = String(ticketRef);
          if (body?.ticketUrl) ticketUrl = String(body.ticketUrl);
          if (body?.ticketToken) ticketToken = String(body.ticketToken);
        } catch {
          // Empty or non-JSON body is fine.
        }
        return { accepted: true, mode: 'remote', ticketRef, ticketUrl, ...(ticketToken ? { ticketToken } : {}) };
      } catch (error: any) {
        logger.warn('[askAi] triage request failed', error);
        return { accepted: false, mode: 'remote', error: error?.message || 'request failed' };
      }
    },
  };
}
